import { deburr } from 'es-toolkit';
import type { SideNavChildItem, SideNavGroup, SideNavItem } from './SideNav';

/** A half-open `[start, end)` range of a label, in UTF-16 code units of the original text. */
export interface SideNavMatchRange {
  start: number;
  end: number;
}

/** One rendered child row. `isHit` is false when the child shows only because it is expanded. */
export interface SideNavChildView {
  item: SideNavChildItem;
  isHit: boolean;
}

/** One rendered top-level row and the children shown under it (empty when collapsed). */
export interface SideNavItemView {
  item: SideNavItem;
  /** False when the item shows only as the parent of a matching child. */
  isHit: boolean;
  children: SideNavChildView[];
}

export interface SideNavGroupView {
  group: SideNavGroup;
  items: SideNavItemView[];
}

/** What the nav renders for one query. */
export interface SideNavView {
  /** Normalized query words; empty when nothing is typed. */
  words: string[];
  groups: SideNavGroupView[];
  footer: SideNavItemView[];
  /** The first matching item in display order — what Enter opens. */
  firstHit: SideNavChildItem | undefined;
  /** True when a query is typed and nothing matches. */
  isEmpty: boolean;
}

/** Lower-cased text without diacritics, for comparing a query with labels. */
export function normalizeSearchText(value: string): string {
  return deburr(value).toLowerCase();
}

/** Splits a query into normalized words; every word must match (AND). */
export function parseSearchQuery(query: string): string[] {
  return normalizeSearchText(query).split(/\s+/).filter(Boolean);
}

function haystackOf(item: SideNavChildItem, groupLabel: string | undefined): string {
  return normalizeSearchText([item.label, ...(item.keywords ?? []), groupLabel ?? ''].join(' '));
}

function matches(
  item: SideNavChildItem,
  groupLabel: string | undefined,
  words: readonly string[]
): boolean {
  const haystack = haystackOf(item, groupLabel);
  return words.every(word => haystack.includes(word));
}

/**
 * Ranges of `text` matched by any of the normalized `words`, merged and in order.
 * Ranges point into the original text, so a highlight keeps its diacritics.
 */
export function findMatchRanges(text: string, words: readonly string[]): SideNavMatchRange[] {
  if (words.length === 0 || text.length === 0) return [];

  // Normalize per character so every normalized position maps back to its source character.
  let normalized = '';
  const sourceIndex: number[] = [];
  const sourceLength: number[] = [];
  let offset = 0;
  for (const char of text) {
    const part = normalizeSearchText(char);
    for (let i = 0; i < part.length; i++) {
      sourceIndex.push(offset);
      sourceLength.push(char.length);
    }
    normalized += part;
    offset += char.length;
  }

  const ranges: SideNavMatchRange[] = [];
  for (const word of words) {
    let from = normalized.indexOf(word);
    while (from !== -1) {
      const last = from + word.length - 1;
      let end = sourceIndex[last] + sourceLength[last];
      // Combining marks after the match normalize to nothing; keep them with their letter.
      while (end < text.length && normalizeSearchText(text.slice(end, end + 1)) === '') end++;
      ranges.push({ start: sourceIndex[from], end });
      from = normalized.indexOf(word, from + word.length);
    }
  }

  ranges.sort((a, b) => a.start - b.start || a.end - b.end);
  const merged: SideNavMatchRange[] = [];
  for (const range of ranges) {
    const previous = merged[merged.length - 1];
    if (previous && range.start <= previous.end) {
      previous.end = Math.max(previous.end, range.end);
    } else {
      merged.push({ ...range });
    }
  }
  return merged;
}

function viewItems(
  items: readonly SideNavItem[],
  groupLabel: string | undefined,
  words: readonly string[],
  activeId: string | undefined
): SideNavItemView[] {
  const searching = words.length > 0;
  const views: SideNavItemView[] = [];

  for (const item of items) {
    const children = item.children ?? [];

    if (!searching) {
      const expanded =
        activeId !== undefined &&
        (item.id === activeId || children.some(child => child.id === activeId));
      views.push({
        item,
        isHit: false,
        children: expanded ? children.map(child => ({ item: child, isHit: false })) : []
      });
      continue;
    }

    const isHit = matches(item, groupLabel, words);
    const childHits = children.filter(child => matches(child, groupLabel, words));
    if (!isHit && childHits.length === 0) continue;
    views.push({
      item,
      isHit,
      children: childHits.map(child => ({ item: child, isHit: true }))
    });
  }

  return views;
}

function firstHitOf(views: readonly SideNavItemView[]): SideNavChildItem | undefined {
  for (const view of views) {
    if (view.isHit) return view.item;
    const child = view.children.find(c => c.isHit);
    if (child) return child.item;
  }
  return undefined;
}

/**
 * Filters the nav: every query word must be in the label, keywords or group label. Children show
 * when the parent or a child is active, or, while searching, when they match.
 */
export function filterSideNav(
  groups: readonly SideNavGroup[],
  footer: readonly SideNavItem[],
  query: string,
  activeId?: string
): SideNavView {
  const words = parseSearchQuery(query);

  const groupViews = groups
    .map(group => ({ group, items: viewItems(group.items, group.label, words, activeId) }))
    .filter(view => view.items.length > 0);
  const footerViews = viewItems(footer, undefined, words, activeId);

  const firstHit =
    groupViews.reduce<SideNavChildItem | undefined>(
      (found, view) => found ?? firstHitOf(view.items),
      undefined
    ) ?? firstHitOf(footerViews);

  return {
    words,
    groups: groupViews,
    footer: footerViews,
    firstHit,
    isEmpty: words.length > 0 && groupViews.length === 0 && footerViews.length === 0
  };
}
